import DialogComponent from 'components/Dialog';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {DialogTypes} from 'common/names';
import FormikNumberField from 'components/inputs/FormikNumberField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {usePostApi} from 'common/hooks/usePostApi';
import Loader from 'components/Loader';
import Typography from '@mui/material/Typography';
import Panel from 'components/Panel';
import Formik from 'components/hook-form/Formik';
import {ILdapSettings, LdapNames, LdapSettings} from 'types/diplomat/LdapSettings';
import FormikTextField from 'components/inputs/FormikTextField';
import {Grid, IconButton, Tab, Tabs} from '@mui/material';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {GridCentered} from 'components/GridCentered';
import FormikRadioGroup from 'components/inputs/FormikRadioGroup';
import {enums} from 'types/enums';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {LdapQueryNames} from 'types/diplomat/LdapQueryParams';
import {a11yProps, TabPanel} from 'components/tabs/TabPanel';
import {useSelector} from 'react-redux';
import {sftpUserSelectors} from 'redux/sftp-user';
import {layoutSelectors} from 'features/layout/redux';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import RefreshIcon from '@mui/icons-material/Refresh';
import {colors} from 'common/colors';
import {castToSend} from 'common/yup/castToSend';
import Button from 'components/Button';
import {useFormContext} from 'react-hook-form';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import {useModal} from 'common/hooks/useModal';

type LDAPSettingsDialogProps = {
   onClose: () => void;
};

const LDAPSettingsDialog = ({onClose}: LDAPSettingsDialogProps) => {
   return (
      <DialogComponent
         maxWidth="sm"
         header={DialogTypes.LDAPSettingsDialog.dialogName}
         close={onClose}
      >
         <LDAPSettingsForm onClose={onClose} />
      </DialogComponent>
   );
};

export const LDAPSettingsForm = ({onClose}: LDAPSettingsDialogProps) => {
   const {t} = useTranslation();
   const {request: fetchLdapSettings, state: session, loading} = useGetApi<ILdapSettings>(
      api.ldapSettings
   );
   const {request: saveLdapSettings} = usePostApi(api.ldapSettings, {
      onSuccess: onClose,
   });

   useEffect(() => {
      fetchLdapSettings();
   }, [fetchLdapSettings]);

   const saveSettings = (values: ILdapSettings) => {
      // console.debug('save ldap', values);
      const payload = castToSend(LdapSettings, values);
      saveLdapSettings(JSON.stringify(payload));
   };
   return loading || !session ? (
      <Loader />
   ) : (
      <Formik
         initialValues={LdapSettings.cast(session) as ILdapSettings}
         validationSchema={LdapSettings}
         onSubmit={saveSettings}
         enableReinitialize
      >
         <>
            <Typography gutterBottom>
               {t(
                  'Specify the LDAP parameters for user authentication on the Diplomat MFT Server.\nDiplomat MFT only supports secure connections using LDAPS, for your safety.'
               )}
            </Typography>
            <LdapServerConnection fetchLdapSettings={fetchLdapSettings} />
            <UserAccountSettings />

            <FormFooterButtons submitLabel={t('Save')} cancel={onClose} skipPermissionCheck />
         </>
      </Formik>
   );
};

const LdapServerConnection = ({fetchLdapSettings}: {fetchLdapSettings: () => void}) => {
   const {t} = useTranslation();
   const certs = useSelector(layoutSelectors.serverSslKeys);
   const isVerifyCert = useFieldValue<ILdapSettings>(LdapNames.verifySslCert);

   const {request: refresh} = usePostApi(api.ldapRefresh, {
      onSuccess: () => {
         fetchLdapSettings();
      },
   });

   return (
      <Panel name={t('LDAP Server Connection')}>
         <IconButton
            onClick={() => refresh(null)}
            style={{
               position: 'absolute',
               right: '1rem',
               top: '0',
               color: colors.success,
            }}
         >
            <RefreshIcon />
         </IconButton>
         <Grid container spacing={2}>
            <Grid item sm={8}>
               <FormikTextField name={LdapNames.address} label={t('Server')} />
            </Grid>
            <Grid item sm={4}>
               <FormikTextField name={LdapNames.port} label={t('Port')} />
            </Grid>
            {/* </Grid>

                     <Grid container spacing={2}> */}
            <GridCentered item sm={4}>
               <FormikCheckbox name={LdapNames.verifySslCert} label={t('Verify certificate')} />
            </GridCentered>
            <Grid item sm={4}>
               <FormikDropdown
                  name={LdapNames.sslCertId}
                  // label={t('Verify certificate')}
                  disabled={!isVerifyCert}
                  options={certs}
               />
            </Grid>
            <Grid item sm={4}>
               <FormikNumberField
                  // style={{textAlign: 'right', width: '190px'}}
                  name={LdapNames.timeout}
                  placeholder={t('Timeout')}
                  InputProps={{endAdornment: t('sec')}}
                  // helperText={t('Timeout')}
                  min={0}
               />
            </Grid>

            {/* <Grid item sm={12}>
                           <Typography gutterBottom>
                              {t(
                                 'User Account for Queries'
                              )}
                           </Typography>
                        </Grid> */}

            <Grid item sm={12}>
               <FormikRadioGroup
                  name={LdapNames.bindingType}
                  label={t('User Account for Queries')}
                  options={enums.bindingType.options}
                  row
               />
            </Grid>

            <Grid item sm={8}>
               <FormikTextField name={LdapNames.userDN} label={t('User DN')} />
            </Grid>
            <Grid item sm={8}>
               <FormikPassphrase
                  name={LdapNames.password}
                  label={t('Password')}
                  fetchUrl={api.getPasswordLdap}
               />
            </Grid>
         </Grid>

         {/* <LastUpdatedLabel date={session.timestamp} author={session.lastUser} /> */}
      </Panel>
   );
};

const UserAccountSettings = () => {
   const {t} = useTranslation();
   const [activeTab, setActiveTab] = React.useState(0);
   const handleChange = React.useCallback(
      (event: React.ChangeEvent<{}>, newValue: number) => {
         setActiveTab(newValue);
      },
      [setActiveTab]
   );

   return (
      <Panel name={t('User Account Settings')}>
         <Tabs variant="scrollable" value={activeTab} onChange={handleChange} aria-label="Triggers">
            <Tab label={t('File Transfer Users')} {...a11yProps(0, 'sftpUsers')} />
            <Tab label={t('Admin Users')} {...a11yProps(1, 'adminUsers')} />
         </Tabs>
         <TabPanel
            key={0}
            value={activeTab}
            index={0}
            prefix={'sftpUsers'}
            // className={classes.tabPanel}
         >
            <SftpUsers isSftp />
         </TabPanel>
         <TabPanel
            key={1}
            value={activeTab}
            index={1}
            prefix={'adminUsers'}
            // className={classes.tabPanel}
         >
            <SftpUsers />
         </TabPanel>
      </Panel>
   );
};

const SftpUsers = ({isSftp}: {isSftp?: boolean}) => {
   const {t} = useTranslation();
   const prefix = isSftp ? LdapNames.sftpQueryParams : LdapNames.adminQueryParams;
   const groups = useSelector(sftpUserSelectors.getSftpGroupNames);

   return (
      <Grid container>
         <Grid item sm={12}>
            <FormikTextField name={`${prefix}.${LdapQueryNames.baseDN}`} label={t('Base DN')} />
         </Grid>
         <Grid item sm={12}>
            <FormikTextField
               name={`${prefix}.${LdapQueryNames.userFilter}`}
               label={t('User Filter')}
            />
         </Grid>
         <Grid item sm={12}>
            <FormikTextField
               name={`${prefix}.${LdapQueryNames.usernameAttribute}`}
               label={t('Username Attribute')}
            />
         </Grid>
         <Grid item sm={12}>
            {isSftp ? (
               <FormikDropdown
                  name={`${prefix}.${LdapQueryNames.vfsGroup}`}
                  label={t('Assigned SFTP Group')}
                  options={groups}
               />
            ) : (
               <FormikDropdown
                  name={LdapNames.assignedRole}
                  label={t('Assigned Role')}
                  options={enums.role.options}
               />
            )}
         </Grid>
         <Grid item>
            <TestLdapButton isSftp={isSftp} />
         </Grid>
      </Grid>
   );
};

const TestLdapButton = ({isSftp}: {isSftp?: boolean}) => {
   const {t} = useTranslation();
   const {
      getValues,
      formState: {isDirty},
   } = useFormContext<ILdapSettings>();
   const {request, state, loading, error} = usePostApi(api.ldapTest);

   const {open, handleOpen, handleClose} = useModal();
   const test = React.useCallback(() => {
      const ldapSettings = getValues();
      request(
         JSON.stringify({
            ldapSettings: isDirty ? castToSend(LdapSettings, ldapSettings) : null,
            queryType: isSftp ? enums.ldapType.enum.SFTP : enums.ldapType.enum.ADMIN,
         })
      );
      handleOpen();
   }, [getValues, isDirty, isSftp, handleOpen]);

   const isError = error || state?.errors?.length > 0;

   let content = t('Testing LDAP server...');
   if (!loading && (state || error)) {
      content = isError
         ? t('Error querying LDAP Server: {{error}}', {
              error: error || state.errors?.join('; '),
           })
         : t('Successful query, {{count}} users found:\n\n{{users}}.', {
              count: state?.results?.length,
              users: state?.results?.join(',\n'),
           });
   }

   return (
      <>
         <Button onClick={test}>{t('Test')}</Button>
         {open && (
            <AlertDialog title={t('LDAP Test Results')} content={content} onClose={handleClose} />
         )}
      </>
   );
};

export default LDAPSettingsDialog;
